/** Apps. */
import { z } from 'zod';
import { AppFramework, slug, uuid } from './common.js';

export const AppCreateRequest = z.object({
  projectId: uuid,
  repositoryId: uuid.optional(),
  slug,
  name: z.string().min(1).max(120),
  framework: AppFramework,
  workingDirectory: z.string().max(200).default('.'),
  configPath: z.string().max(200).default('hangar.yml'),
  easProjectId: z.string().max(100).optional(),
  bitriseAppSlug: z.string().max(100).optional(),
});
export type AppCreateRequest = z.infer<typeof AppCreateRequest>;

/** An Expo project slug: letters, digits, hyphens and underscores (`expo.slug` of the app config). */
export const EAS_PROJECT_SLUG_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,99}$/;

/**
 * `POST /apps/{appId}/eas/provision` (SPEC §9.1.1): Hangar creates the app's EAS project under the Expo account Malible manages.
 * `providerAccountId` picks the EAS account when the org has several; `slug` is the project slug when the app config's
 * `expo.slug` differs from the Hangar app's slug (eas-cli refuses a project whose slug differs from `expo.slug`).
 */
export const EasProvisionRequest = z.object({
  providerAccountId: uuid.optional(),
  slug: z.string().regex(EAS_PROJECT_SLUG_PATTERN, 'letters, digits, hyphens and underscores, up to 100 chars').optional(),
}).strict();
export type EasProvisionRequest = z.infer<typeof EasProvisionRequest>;

/**
 * `POST /apps/{appId}/eas/webhook` (SPEC §24): register (or repair) Hangar's BUILD webhook on the app's EAS project, whatever
 * way the project was linked (created by Hangar or typed in by hand). `providerAccountId` picks the EAS account whose token is
 * used when the org has several (the answer of a project Hangar created defaults to the account it was created with).
 */
export const EasWebhookRequest = z.object({
  providerAccountId: uuid.optional(),
}).strict();
export type EasWebhookRequest = z.infer<typeof EasWebhookRequest>;

export const EasSetupSource = z.enum(['hangar_created', 'hangar_reused', 'manual']);
export type EasSetupSource = z.infer<typeof EasSetupSource>;

/**
 * What registering Hangar's webhook at the provider came to (SPEC §24): `registered`, `skipped` (nothing to register with, e.g. no
 * public hooks URL) or `failed` (the provider refused). Never blocks the setup it belongs to; `reason` is worded for people.
 */
export const WebhookRegistration = z.object({
  status: z.enum(['registered', 'skipped', 'failed']),
  at: z.string(),
  reason: z.string().nullable(),
});
export type WebhookRegistration = z.infer<typeof WebhookRegistration>;

/** `GET /apps/{appId}/eas` and the answer of `POST /apps/{appId}/eas/provision`. */
export const EasSetup = z.object({
  appId: uuid,
  configured: z.boolean(),
  /** How the app got its `easProjectId`: created or found by Hangar, or typed in by hand (`manual`: owner and slug unknown). */
  source: EasSetupSource.nullable(),
  projectId: z.string().nullable(),
  owner: z.string().nullable(),
  slug: z.string().nullable(),
  fullName: z.string().nullable(),
  dashboardUrl: z.string().nullable(),
  providerAccountId: uuid.nullable(),
  provisionedAt: z.string().nullable(),
  /** True when the request found the app already configured and changed nothing. */
  alreadyProvisioned: z.boolean().optional(),
  /** What the app's `app.json` must carry (`expo.owner`, `expo.slug`, `expo.extra.eas.projectId`), or null before the project exists. */
  appConfig: z.object({ owner: z.string(), slug: z.string(), extra: z.object({ eas: z.object({ projectId: z.string() }) }) }).nullable(),
  /** The slug a provision without `slug` would create (`<org slug>-<app slug>`), or null once the app has a project. */
  proposedSlug: z.string().nullable(),
  /** Hangar's BUILD webhook on the project; null until a registration was attempted. */
  webhook: WebhookRegistration.nullable().optional(),
});
export type EasSetup = z.infer<typeof EasSetup>;

export const AppPatch = z.object({
  name: z.string().min(1).max(120).optional(),
  workingDirectory: z.string().max(200).optional(),
  configPath: z.string().max(200).optional(),
  easProjectId: z.string().max(100).nullable().optional(),
  bitriseAppSlug: z.string().max(100).nullable().optional(),
  archived: z.boolean().optional(),
}).strict();
export type AppPatch = z.infer<typeof AppPatch>;

/** A Bitbucket repository slug (the last part of `bitbucket.org/<workspace>/<slug>`). */
export const REPOSITORY_SLUG_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._~-]{0,127}$/;

/**
 * `PUT /apps/{appId}/source` (SPEC §9.2): links the app to a repository of the org's source-control connection, so Hangar's
 * runner can fetch a commit of it for a `hangar_runner` build (Expo / React Native apps). The repository must be one the
 * connection's token can read (checked with Bitbucket before it is stored).
 */
export const AppSourceRequest = z.object({
  scmConnectionId: uuid,
  repository: z.string().regex(REPOSITORY_SLUG_PATTERN, 'a Bitbucket repository slug'),
}).strict();
export type AppSourceRequest = z.infer<typeof AppSourceRequest>;

/** The app's repository: `apps.source`, or the one "Create on Bitrise" recorded (`origin: bitrise_setup`). Holds no secret. */
export const AppSource = z.object({
  provider: z.literal('bitbucket'),
  scmConnectionId: uuid,
  workspace: z.string(),
  repository: z.string(),
  fullName: z.string(),
  defaultBranch: z.string().nullable(),
  origin: z.enum(['app', 'bitrise_setup']),
  linkedAt: z.string().nullable(),
});
export type AppSource = z.infer<typeof AppSource>;

export const App = z.object({
  id: uuid, projectId: uuid, repositoryId: uuid.nullable(), slug, name: z.string(), framework: AppFramework,
  workingDirectory: z.string(), configPath: z.string(), configSha: z.string().nullable(), easProjectId: z.string().nullable(),
  bitriseAppSlug: z.string().nullable(), lastBuildNumber: z.number().int(), createdAt: z.string(), archivedAt: z.string().nullable(), etag: z.string(),
  /** The repository Hangar reads the app's source from (null when none is linked); absent from APIs older than 2026-10-21. */
  source: AppSource.nullable().optional(),
});
export type App = z.infer<typeof App>;
